import React, { useState, useEffect } from 'react';
import Article from './components/Article';
import Sidebar from './components/Sidebar';
import PlatformSelector from './components/PlatformSelector';
import ICCIDGenerator from './components/ICCIDGenerator';
import BuyCoursePage from './components/BuyCoursePage';
import './App.css';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useUser, useClerk, SignIn, SignUp } from '@clerk/clerk-react';
import Success from './components/Success';

function App() {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, user } = useUser();
  const { signOut } = useClerk();
  const [selectedArticle, setSelectedArticle] = useState('/articles/lander.md');
  const [showPlatformSelector, setShowPlatformSelector] = useState(false);
  const [showQuestionnaireResults, setShowQuestionnaireResults] = useState(false);
  const [showBuyPage, setShowBuyPage] = useState(false);
  const [hasPaid, setHasPaid] = useState(false);
  const [metaInitialized, setMetaInitialized] = useState(false);

  // Check payment status when user data changes
  useEffect(() => {
    const initializeUser = async () => {
      if (isLoaded && isSignedIn && user) {
        console.log('App: Checking user metadata:', user.unsafeMetadata);
        
        const userHasPaid = user.unsafeMetadata?.has_paid === true;
        setHasPaid(userHasPaid);
        console.log('App: User payment status:', userHasPaid);

        // Initialize metadata ONLY if it's undefined
        if (user.unsafeMetadata?.has_paid === undefined) {
          console.log('App: Initializing metadata for user:', user.id);
          try {
            await user.update({
              unsafeMetadata: { has_paid: false }
            });
            console.log('App: Successfully initialized metadata with default value');
          } catch (error) {
            console.error('App: Error initializing metadata:', error);
          }
        }
      } else {
        setHasPaid(false);
      }
    };

    initializeUser();
  }, [isLoaded, isSignedIn, user]);

  const handleArticleSelect = (path) => {
    if (path === '/sign-in') {
      navigate('/sign-in');
      return;
    }
    
    setSelectedArticle(path);
    if (path === 'iccid-generator') {
      setShowPlatformSelector(false);
      navigate('/course');
    } else {
      setShowPlatformSelector(false);
      navigate('/course');
    }
  };

  const handleNewbieClick = () => {
    setShowPlatformSelector(true);
    navigate('/course');
  };

  const handleBuyClick = () => {
    setShowBuyPage(true);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const renderContent = (page) => {
    if (!isLoaded) {
      return <div>Loading...</div>;
    }

    // Always show landing page on root route
    if (page === 'main') {
      return <Article articlePath="/articles/lander.md" />;
    }
    
    // For course content, check authentication first
    if (!isSignedIn && page === 'course') {
      return (
        <div className="auth-required">
          <h2>Authentication Required</h2>
          <p>Please log in to access this content.</p>
          <button 
            className="login-redirect-button" 
            onClick={() => navigate('/sign-in')}
          >
            Go to Login
          </button>
        </div>
      );
    }

    // For authenticated users, check payment status
    if (page === 'course' && !hasPaid) {
      return (
        <div className="payment-required">
          <h2>Payment Required</h2>
          <p>Please purchase the course to access this content.</p>
          <button 
            className="buy-redirect-button" 
            onClick={() => setShowBuyPage(true)}
          >
            Buy Course Access
          </button>
        </div>
      );
    }

    // Show content for authenticated and paid users
    if (page === 'course') {
      if (showPlatformSelector) {
        return (
          <PlatformSelector 
            onArticleSelect={handleArticleSelect}
          />
        );
      } else if (selectedArticle === 'iccid-generator') {
        return <ICCIDGenerator />;
      } else {
        return <Article articlePath={selectedArticle} />;
      }
    }

    return <Navigate to="/" />;
  };

  return (
    <div className="app">
      <Sidebar 
        onArticleSelect={handleArticleSelect}
        onNewbieClick={handleNewbieClick}
        isAuthenticated={isSignedIn}
        onBuyClick={handleBuyClick}
        onLogout={handleLogout}
      />
      <main className="content">
        <Routes>
          <Route path="/" element={renderContent('main')} />
          <Route path="/course" element={renderContent('course')} />
          <Route 
            path="/sign-in" 
            element={
              <SignIn 
                signUpUrl="/sign-up"
                appearance={{
                  baseTheme: "dark",
                  elements: {
                    formButtonPrimary: 'submit-button',
                    card: 'clerk-card',
                    headerTitle: 'clerk-header',
                    headerSubtitle: 'clerk-subtitle',
                    socialButtonsBlockButton: 'social-button',
                    formFieldInput: {
                      className: 'clerk-input',
                      style: { color: 'white' }
                    },
                    footerActionLink: 'clerk-footer-link'
                  },
                  layout: {
                    socialButtonsPlacement: 'bottom'
                  },
                  variables: {
                    colorBackground: "#1a2332",
                    colorInputBackground: "#0d1b2a",
                    colorAlphaShade: "#415a77",
                    colorText: "white",
                    colorTextSecondary: "#e0e0e0",
                    colorPrimary: "#415a77",
                    colorInputText: "white"
                  }
                }}
              />
            } 
          />
          <Route 
            path="/sign-up" 
            element={
              <SignUp 
                signInUrl="/sign-in"
                appearance={{
                  baseTheme: "dark",
                  elements: {
                    formButtonPrimary: 'submit-button',
                    card: 'clerk-card',
                    headerTitle: 'clerk-header',
                    headerSubtitle: 'clerk-subtitle',
                    socialButtonsBlockButton: 'social-button',
                    formFieldInput: {
                      className: 'clerk-input',
                      style: { color: 'white' }
                    },
                    footerActionLink: 'clerk-footer-link'
                  },
                  layout: {
                    socialButtonsPlacement: 'bottom',
                    showOptionalFields: false
                  },
                  variables: {
                    colorBackground: "#1a2332",
                    colorInputBackground: "#0d1b2a",
                    colorAlphaShade: "#415a77",
                    colorText: "white",
                    colorTextSecondary: "#e0e0e0",
                    colorPrimary: "#415a77",
                    colorInputText: "white"
                  }
                }}
              />
            } 
          />
          <Route path="/success" element={<Success />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      {showBuyPage && (
        <BuyCoursePage 
          onClose={() => setShowBuyPage(false)} 
          isAuthenticated={isSignedIn}
          userEmail={user?.emailAddresses[0]?.emailAddress}
        />
      )}
    </div>
  );
}

export default App;
